import type { UserId } from '../db/vo.interface';

export type AdminCapability =
  | 'roles:manage'
  | 'roles:view'
  | 'org:view'
  | 'org:edit'
  | 'org:create'
  | 'org:delete'
  | 'user:view'
  | 'user:edit'
  | 'user:disable'
  | 'user:delete'
  | 'sso:manage'
  | 'analytics:view'
  | 'fleet:view'
  | 'fleet:manage'
  | 'test-runs:view';

export type AdminRole =
  | 'owner'
  | 'editor'
  | 'org-editor'
  | 'org-viewer'
  | 'user-editor'
  | 'user-viewer'
  | 'analytics-viewer'
  | 'fleet-operator'
  | 'fleet-viewer';

export const ALL_ADMIN_ROLES: readonly AdminRole[] = [
  'owner',
  'editor',
  'org-editor',
  'org-viewer',
  'user-editor',
  'user-viewer',
  'analytics-viewer',
  'fleet-operator',
  'fleet-viewer',
];

/** Single source of truth: which capabilities each role grants. Code, never DB. */
export const ROLE_CAPABILITIES: Record<AdminRole, readonly AdminCapability[]> = {
  owner: [
    'roles:manage',
    'roles:view',
    'org:view',
    'org:edit',
    'org:create',
    'org:delete',
    'user:view',
    'user:edit',
    'user:disable',
    'user:delete',
    'sso:manage',
    'analytics:view',
    'fleet:view',
    'fleet:manage',
    'test-runs:view',
  ],
  editor: [
    'roles:view',
    'org:view',
    'org:edit',
    'org:create',
    'org:delete',
    'user:view',
    'user:edit',
    'user:disable',
    'user:delete',
    'sso:manage',
    'analytics:view',
    'fleet:view',
    'fleet:manage',
    'test-runs:view',
  ],
  'org-editor': ['org:view', 'org:edit', 'org:create'],
  'org-viewer': ['org:view'],
  'user-editor': ['user:view', 'user:edit', 'user:disable', 'user:delete'],
  'user-viewer': ['user:view'],
  'analytics-viewer': ['analytics:view'],
  'fleet-operator': ['fleet:view', 'fleet:manage'],
  'fleet-viewer': ['fleet:view'],
};

export function isAdminRole(value: unknown): value is AdminRole {
  return typeof value === 'string' && (ALL_ADMIN_ROLES as readonly string[]).includes(value);
}

export function capabilitiesForRoles(roles: readonly AdminRole[]): Set<AdminCapability> {
  const caps = new Set<AdminCapability>();
  for (const role of roles) {
    for (const cap of ROLE_CAPABILITIES[role] ?? []) caps.add(cap);
  }
  return caps;
}

/** RTDB record at /adminStaff/{uid}. `roles` is a set (map-of-true) for atomic per-key writes/validation. */
export interface DbAdminStaffEntry {
  roles: { [R in AdminRole]?: true };
  grantedBy: UserId;
  grantedTs: number;
  updatedBy?: UserId;
  updatedTs?: number;
  email?: string;
}

export function roleSetToList(roleSet: DbAdminStaffEntry['roles'] | undefined | null): AdminRole[] {
  if (!roleSet) return [];
  return (Object.keys(roleSet) as AdminRole[]).filter((r) => roleSet[r] === true && isAdminRole(r));
}

export function listToRoleSet(roles: readonly AdminRole[]): { [R in AdminRole]?: true } {
  const set: { [R in AdminRole]?: true } = {};
  for (const role of roles) {
    if (isAdminRole(role)) set[role] = true;
  }
  return set;
}

/** The custom claim shape the sync trigger writes. `admin` is the coarse "is staff" flag. */
export interface AdminStaffClaims {
  admin: true;
  roles: AdminRole[];
}

/** Derive the cached claim from the DB entry. null => clear the claim (no roles => not staff). */
export function deriveAdminClaim(entry: DbAdminStaffEntry | null | undefined): AdminStaffClaims | null {
  const roles = entry ? roleSetToList(entry.roles) : [];
  if (roles.length === 0) return null;
  return { admin: true, roles };
}

/**
 * Read the assigned roles from a claim's `roles` array. Roles come strictly from `/adminStaff`
 * (assigned to the claim at login / by the trigger) — a bare `admin` flag grants nothing.
 */
export function rolesFromClaim(claims: { roles?: unknown }): AdminRole[] {
  if (Array.isArray(claims.roles)) return claims.roles.filter(isAdminRole);
  return [];
}
